
import { useState, useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import {
    NSCard, NSLabel, NSSelect,
} from 'bricks';
import NSProFormaInput from 'bricks/NSProFormaInput/NSProFormaInput';
import NSSwitch from 'bricks/NSSwitch/NSSwitch';
import CoordinateInputRow from 'components/CoordinateInputRow/CoordinateInputRow';
import ExpandableSection from 'components/ExpandableSection/ExpandableSection';
import RenderIf from 'components/RenderIf/RenderIf';
import { STATES_OPTIONS } from 'constants/statesList';
import { DEFAULT_NYC_COORDINATES } from 'constants/deals';
import {
    FORM_LABEL_CLASS_NAME, FORM_ROW_CLASS_NAME,
    GENERAL_INFORMATION_EXPANDABLE_SECTIONS, HIDE_WARNINGS_KEY, ProjectActualsDiscrepanciesEnum,
} from 'views/ProFormaTable/constants';
import Map from 'components/Map/Map';
import useDebounce from 'hooks/useDebounce';
import useGetCoordinates from 'queries/Mapbox/useGetCoordinates';
import {
    handleDateChange, calculateFloorAreaRatio, convertSignUpDataToProFormaValues,
} from 'views/ProFormaTable/helpers';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import {
    IProFormaValues, IProFormaGeneralInfo, IGrossBuildableAreaLotsData, BOEProFormaDiscrepancies,
} from 'views/ProFormaTable/types';
import { ProFormaQuickStartValues } from 'views/public/ProFormaQuickStart/types';
import ProFormaSizeDetails from './ProFormaSizeDetails/ProFormaSizeDetails';
import { useProFormaSocketContext } from '../socketContext/ProFormaSocketProvider';
import ProFormaKeyDates from './ProFormaKeyDates/ProFormaKeyDates';
import BOEDiscrepancyIcon from '../../BOEDiscrepancyIcon/BOEDiscrepancyIcon';
import { isLandDevelopmentModel, isStudentHousingModel } from 'helpers/proForma';

export interface IProFormaGeneralInformationProps {
    hasWriteAccessToProForma: boolean;
    signUpMode?: boolean;
    proFormaSignUpData?: ProFormaQuickStartValues;
    setProFormaSignUpData?: (data: ProFormaQuickStartValues) => void;
    boeDiscrepancies?: BOEProFormaDiscrepancies;
}

const ProFormaGeneralInformation = ({
    hasWriteAccessToProForma,
    signUpMode = false,
    proFormaSignUpData,
    setProFormaSignUpData,
    boeDiscrepancies,
}: IProFormaGeneralInformationProps) => {
    const {
        proForma, handleUpdateProForma, buildableLots, shouldUseProjectActuals, projectActuals,
        getLocalStorageValueByKey,
    } = useProFormaSocketContext();

    const isLandDevelopment = isLandDevelopmentModel(proForma.proFormaModelId);

    const actualsDiscrepancies = projectActuals?.projectActuals?.discrepancies || [];

    const localStorageHideBOEWarnings = Boolean(getLocalStorageValueByKey(HIDE_WARNINGS_KEY));

    const parseProFormaData = (updatedProForma: IProFormaGeneralInfo, updatedBuildableLots: IGrossBuildableAreaLotsData) => {
        let grossBuildableArea = updatedProForma.grossBuildableArea !== null ? Number(updatedProForma.grossBuildableArea) : null;
        let floorAreaRatio = updatedProForma.floorAreaRatio !== null ? Number(updatedProForma.floorAreaRatio) : null;
        const landArea = updatedProForma.landArea !== null ? Number(updatedProForma.landArea) : null;
        if (updatedProForma.calculateArea) {
            grossBuildableArea = Number(updatedBuildableLots.totalGrossBuildableArea || 0);

            floorAreaRatio = calculateFloorAreaRatio(
                landArea,
                updatedProForma.landAreaUnitTypeId,
                grossBuildableArea,
            );
        }

        const initialValues: IProFormaValues = {
            id: updatedProForma.id,
            address: updatedProForma.address,
            city: updatedProForma.city,
            county: updatedProForma.county,
            state: updatedProForma.state,
            zipCode: updatedProForma.zipCode,
            associatedUniversity: updatedProForma.associatedUniversity,
            startDate: updatedProForma.startDate ? updatedProForma.startDate.split('T')[0] : '',
            landArea,
            landAreaUnitTypeId: updatedProForma.landAreaUnitTypeId,
            saleDate: updatedProForma.saleDate ? updatedProForma.saleDate.split('T')[0] : '',
            saleMonth: updatedProForma.saleMonth,
            floorAreaRatio,
            grossBuildableArea,
            buildings: Number(updatedProForma.buildings),
            averageStories: Number(updatedProForma.averageStories),
            averageClearHeight: updatedProForma.averageClearHeight ? Number(updatedProForma.averageClearHeight) : null,
            isFloorPlateAreaVaried: updatedProForma.isFloorPlateAreaVaried,
            storyArea: updatedProForma.storyArea,
            builtGrossSquareFootage: updatedProForma.builtGrossSquareFootage ? Number(updatedProForma.builtGrossSquareFootage) : null,
            retailBuiltGrossSquareFootage: updatedProForma.retailBuiltGrossSquareFootage ? Number(updatedProForma.retailBuiltGrossSquareFootage) : null,
            structuredParkingSpaces: updatedProForma.structuredParkingSpaces,
            surfaceParkingSpaces: updatedProForma.surfaceParkingSpaces,
            undergroundParkingSpaces: updatedProForma.undergroundParkingSpaces,
            trailerParkingSpaces: updatedProForma.trailerParkingSpaces,
            calculateArea: updatedProForma.calculateArea,
            areaMode: updatedProForma.areaMode,
            constructionStart: updatedProForma.constructionStart ? updatedProForma.constructionStart.split('T')[0] : null,
            constructionDuration: updatedProForma.constructionDuration ? Number(updatedProForma.constructionDuration) : null,
            holdPeriod: updatedProForma.holdPeriod ? Number(updatedProForma.holdPeriod) : null,
            efficiencyRatio: updatedProForma.efficiencyRatio != null ? Number(updatedProForma.efficiencyRatio) * 100 : null,
            retailEfficiencyRatio: updatedProForma.retailEfficiencyRatio != null ? Number(updatedProForma.retailEfficiencyRatio) * 100 : null,
            estimatedUnits: updatedProForma.estimatedUnits != null ? Number(updatedProForma.estimatedUnits) : null,
            estimatedRacks: updatedProForma.estimatedRacks != null ? Number(updatedProForma.estimatedRacks) : null,
            powerCapacityKw: updatedProForma.powerCapacityKw != null ? Number(updatedProForma.powerCapacityKw) : null,
            latitude: updatedProForma.latitude != null ? Number(updatedProForma.latitude) : null,
            longitude: updatedProForma.longitude != null ? Number(updatedProForma.longitude) : null,
            useCoordinates: Boolean(updatedProForma.useCoordinates),
        };
        return initialValues;
    };

    const [values, setValues] = useState<IProFormaValues>(parseProFormaData(proForma, buildableLots));

    const setInitialProFormaValues = () => {
        if (proForma.id) {
            setValues(parseProFormaData(proForma, buildableLots));
        }
        if (signUpMode && proFormaSignUpData) {
            setValues(convertSignUpDataToProFormaValues(proFormaSignUpData));
        }
    };

    const handleInputChange = (name: string, value: any) => {
        let updatedValues = { ...values, [name]: value };
        console.log(`Updated ${name} to ${value}`);

        if (name === 'startDate' && values.constructionStart) {
            const [year, month] = value.split('-');
            const [constructionYear, constructionMonth] = values.constructionStart.split('-');
            const isStartLaterThanConstructionStart = year > constructionYear || (year === constructionYear && month > constructionMonth);

            if (isStartLaterThanConstructionStart) {
                updatedValues = { ...updatedValues, constructionStart: value };
            }
        }

        setValues(updatedValues);

        if (signUpMode && proFormaSignUpData && setProFormaSignUpData) {
            setProFormaSignUpData({
                ...proFormaSignUpData,
                basicInformation: {
                    ...proFormaSignUpData?.basicInformation,
                    [name]: value,
                },
            });
        }
    };

    const fullAddressAsString = [values.address, values.city, values.state, values.zipCode]
        .filter(part => part && String(part).trim())
        .join(', ');

    const debouncedAddress = useDebounce(fullAddressAsString, 500);
    const addressGeocodeEnabled = !values.useCoordinates;
    const {
        data: coordinates = {},
        isLoading: isLoadingMapCoordinates,
    } = useGetCoordinates(addressGeocodeEnabled ? debouncedAddress : '');

    const debouncedLatitude = useDebounce(values.latitude, 500);
    const debouncedLongitude = useDebounce(values.longitude, 500);

    const isLatitudeInRange = (value: number | null | undefined): value is number => (
        typeof value === 'number' && Number.isFinite(value) && value >= -90 && value <= 90
    );
    const isLongitudeInRange = (value: number | null | undefined): value is number => (
        typeof value === 'number' && Number.isFinite(value) && value >= -180 && value <= 180
    );

    const latitudeError = values.latitude != null && !isLatitudeInRange(values.latitude)
        ? 'Latitude must be between -90 and 90'
        : null;
    const longitudeError = values.longitude != null && !isLongitudeInRange(values.longitude)
        ? 'Longitude must be between -180 and 180'
        : null;

    let mapCoordinates: { lat: number; lng: number } | null = null;
    if (values.useCoordinates) {
        if (isLatitudeInRange(debouncedLatitude) && isLongitudeInRange(debouncedLongitude)) {
            mapCoordinates = { lat: debouncedLatitude, lng: debouncedLongitude };
        } else {
            mapCoordinates = DEFAULT_NYC_COORDINATES;
        }
    } else if (coordinates?.lat != null && coordinates?.lng != null) {
        mapCoordinates = coordinates;
    } else if (fullAddressAsString.trim() === '') {
        mapCoordinates = DEFAULT_NYC_COORDINATES;
    }

    useEffect(() => {
        setInitialProFormaValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proForma, proFormaSignUpData]);

    useEffect(() => {
        if (values.zipCode && coordinates?.county
            && (values.county === undefined || values.county === null || values.county === '')) {
            setValues({ ...values, county: coordinates.county });
            handleUpdateProForma([{ key: 'county', value: coordinates.county }]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coordinates]);

    return (
        <ReadOnlyWrapper isReadOnly={!hasWriteAccessToProForma && !signUpMode}>
            <ExpandableSection
                number={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[0].number}
                title={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[0].title}
                subtitle={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[0].subtitle}
                isExpanded
                animate
            >
                <NSCard className="p-3 NSCard--level-2">
                    <Row>
                        <Col>
                            <div className={FORM_ROW_CLASS_NAME}>
                                <NSLabel className={FORM_LABEL_CLASS_NAME}>Deal name</NSLabel>
                                <NSProFormaInput
                                    type="text"
                                    name="dealName"
                                    placeholder="Enter deal name"
                                    value={signUpMode ? values.dealName : proForma.dealName}
                                    disabled={!signUpMode}
                                    handleUpdateValue={value => handleInputChange('dealName', value)}
                                />
                            </div>
                            <RenderIf isTrue={!values.useCoordinates}>
                                <div className={FORM_ROW_CLASS_NAME}>
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Address</NSLabel>
                                    <NSProFormaInput
                                        type="text"
                                        name="address"
                                        value={values.address}
                                        handleUpdateValue={value => {
                                            handleInputChange('address', value);
                                            handleUpdateProForma([{ key: 'address', value }]);
                                        }}
                                        placeholder="Enter address"
                                    />
                                </div>
                                <div className={FORM_ROW_CLASS_NAME}>
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>City</NSLabel>
                                    <NSProFormaInput
                                        type="text"
                                        name="city"
                                        value={values.city}
                                        handleUpdateValue={value => {
                                            handleInputChange('city', value);
                                            handleUpdateProForma([{ key: 'city', value }]);
                                        }}
                                        placeholder="Enter city"
                                    />
                                </div>
                                <div className={FORM_ROW_CLASS_NAME}>
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>County</NSLabel>
                                    <NSProFormaInput
                                        type="text"
                                        name="county"
                                        value={values.county}
                                        handleUpdateValue={value => {
                                            handleInputChange('county', value);
                                            handleUpdateProForma([{ key: 'county', value }]);
                                        }}
                                        placeholder="Enter county"
                                    />
                                </div>
                                <div className={FORM_ROW_CLASS_NAME}>
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>State</NSLabel>
                                    <div className="col-4 pl-0">
                                        <NSSelect
                                            name="state"
                                            options={STATES_OPTIONS}
                                            value={{ label: values.state, value: values.state }}
                                            onChange={option => handleInputChange('state', option.value)}
                                            onBlur={() => handleUpdateProForma([{ key: 'state', value: values.state }])}
                                            isClearable={false}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Zip</NSLabel>
                                    <div className="col-4 pl-0">
                                        <NSProFormaInput
                                            type="text"
                                            name="zipCode"
                                            value={values.zipCode}
                                            handleUpdateValue={value => {
                                                handleInputChange('zipCode', value);
                                                handleUpdateProForma([{ key: 'zipCode', value }]);
                                            }}
                                            placeholder="Enter zip code"
                                        />
                                    </div>
                                </div>
                            </RenderIf>
                            <RenderIf isTrue={values.useCoordinates}>
                                <CoordinateInputRow
                                    name="latitude"
                                    label="Latitude"
                                    value={values.latitude ?? null}
                                    min={-90}
                                    max={90}
                                    rowClassName={FORM_ROW_CLASS_NAME}
                                    labelClassName={FORM_LABEL_CLASS_NAME}
                                    onCommit={next => {
                                        handleInputChange('latitude', next);
                                        if (next === null || isLatitudeInRange(next)) {
                                            handleUpdateProForma([{ key: 'latitude', value: next }]);
                                        }
                                    }}
                                />
                                <RenderIf isTrue={Boolean(latitudeError)}>
                                    <div className="d-flex mb-2">
                                        <div className={FORM_LABEL_CLASS_NAME} />
                                        <p className="text-danger small mt-0 mb-0">{latitudeError}</p>
                                    </div>
                                </RenderIf>
                                <CoordinateInputRow
                                    name="longitude"
                                    label="Longitude"
                                    value={values.longitude ?? null}
                                    min={-180}
                                    max={180}
                                    rowClassName={FORM_ROW_CLASS_NAME}
                                    labelClassName={FORM_LABEL_CLASS_NAME}
                                    onCommit={next => {
                                        handleInputChange('longitude', next);
                                        if (next === null || isLongitudeInRange(next)) {
                                            handleUpdateProForma([{ key: 'longitude', value: next }]);
                                        }
                                    }}
                                />
                                <RenderIf isTrue={Boolean(longitudeError)}>
                                    <div className="d-flex mb-2">
                                        <div className={FORM_LABEL_CLASS_NAME} />
                                        <p className="text-danger small mt-0 mb-0">{longitudeError}</p>
                                    </div>
                                </RenderIf>
                            </RenderIf>
                            <div className="my-2">
                                <NSSwitch
                                    id="useCoordinates"
                                    name="useCoordinates"
                                    data-testid="useCoordinatesSwitch"
                                    checked={!!values.useCoordinates}
                                    onChange={e => {
                                        handleInputChange('useCoordinates', e.target.checked);
                                        handleUpdateProForma([{ key: 'useCoordinates', value: e.target.checked }]);
                                    }}
                                    label="Use coordinates"
                                />
                            </div>
                            {isStudentHousingModel(proForma.proFormaModelId) && (
                                <div className="d-flex mt-2">
                                    <NSLabel className={FORM_LABEL_CLASS_NAME}>Associated university</NSLabel>
                                    <div className="col-4 pl-0">
                                        <NSProFormaInput
                                            type="text"
                                            name="associatedUniversity"
                                            value={values.associatedUniversity || ''}
                                            handleUpdateValue={value => {
                                                handleInputChange('associatedUniversity', value);
                                                handleUpdateProForma([{ key: 'associatedUniversity', value }]);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </Col>
                        <Col className="NSCard--level-3 mx-2 p-0">
                            <RenderIf isTrue={!!mapCoordinates} isLoading={isLoadingMapCoordinates}>
                                {mapCoordinates && <Map lat={mapCoordinates.lat} lng={mapCoordinates.lng} height="200px" />}
                            </RenderIf>
                        </Col>
                    </Row>
                </NSCard>
            </ExpandableSection>

            <ExpandableSection
                number={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[1].number}
                title={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[1].title}
                subtitle={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[1].subtitle}
                isExpanded
                animate
            >
                <ProFormaKeyDates
                    shouldUseProjectActuals={shouldUseProjectActuals}
                    actualsDiscrepancies={actualsDiscrepancies as ProjectActualsDiscrepanciesEnum[]}
                    values={values}
                    handleInputChange={handleInputChange}
                    handleDateChange={handleDateChange}
                    handleUpdateProForma={handleUpdateProForma}
                    proFormaSignUpData={proFormaSignUpData}
                />
            </ExpandableSection>

            <ExpandableSection
                number={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[2].number}
                title={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[2].title}
                subtitle={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[2].subtitle}
                warningIcon={(
                    <BOEDiscrepancyIcon
                        section={GENERAL_INFORMATION_EXPANDABLE_SECTIONS[2].title}
                        metricNames={boeDiscrepancies ? boeDiscrepancies[GENERAL_INFORMATION_EXPANDABLE_SECTIONS[2].number] : []}
                        className="mr-1 my-auto"
                    />
                )}
                showWarningIcon={boeDiscrepancies && !localStorageHideBOEWarnings
                    ? !!boeDiscrepancies[GENERAL_INFORMATION_EXPANDABLE_SECTIONS[2].number]?.length : false}
                isExpanded
                animate
            >
                <RenderIf isTrue={!isLandDevelopment}>
                    <NSCard className="p-3 NSCard--level-2">
                        <ProFormaSizeDetails
                            values={values}
                            handleInputChange={handleInputChange}
                            actualsDiscrepancies={actualsDiscrepancies}
                            isSignUpMode={signUpMode}
                        />
                    </NSCard>
                </RenderIf>
            </ExpandableSection>
        </ReadOnlyWrapper>
    );
};

export default ProFormaGeneralInformation;
